import React, { useState } from 'react';
import { 
    Briefcase, 
    CheckCircle, 
    Star, 
    TrendingUp, 
    Calendar,
    Award,
    AlertCircle,
    History,
    DollarSign,
    ArrowUp,
    ArrowDown
} from 'lucide-react';
import PromotionModal from './PromotionModal';
import PromotionHistoryModal from './PromotionHistoryModal';

export default function ContractorMetricsCard({ contractor, onUpdate }) {
    const [showPromotionModal, setShowPromotionModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    const metrics = contractor.metrics || {};
    const phase = contractor.phase || 'trainee';

    const getPhaseInfo = (phase) => {
        const phases = {
            'shadow': { name: 'Shadow', color: 'gray', bgColor: 'bg-gray-50', textColor: 'text-gray-800', borderColor: 'border-gray-200' },
            'trainee': { name: 'Trainee', color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-800', borderColor: 'border-blue-200' },
            'junior': { name: 'Junior Tech', color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-800', borderColor: 'border-purple-200' },
            'standard': { name: 'Standard Tech', color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-800', borderColor: 'border-green-200' },
            'experienced': { name: 'Experienced Tech', color: 'yellow', bgColor: 'bg-yellow-50', textColor: 'text-yellow-800', borderColor: 'border-yellow-200' }
        };
        return phases[phase] || phases['trainee'];
    };

    const phaseInfo = getPhaseInfo(phase);

    const getCompletionRateColor = (rate) => {
        if (rate >= 95) return 'text-green-600';
        if (rate >= 90) return 'text-yellow-600';
        return 'text-red-600';
    };

    const getRatingColor = (rating) => {
        if (rating >= 4.5) return 'text-green-600';
        if (rating >= 4.0) return 'text-yellow-600';
        return 'text-red-600';
    };

    const hasPromotionHistory = contractor.promotionHistory && contractor.promotionHistory.length > 0;

    return (
        <>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Header with Phase Badge */}
                <div className={`p-4 ${phaseInfo.bgColor} border-b ${phaseInfo.borderColor}`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border-2 border-white shadow">
                                <TrendingUp className={`w-6 h-6 text-${phaseInfo.color}-600`} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900">{contractor.name}</h3>
                                <p className={`text-sm font-medium ${phaseInfo.textColor}`}>
                                    {phaseInfo.name} • ${contractor.hourlyRate || 18}/hr
                                </p>
                            </div>
                        </div>
                        
                        {metrics.eligibleForPromotion && (
                            <div className="flex items-center space-x-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                <Award className="w-4 h-4" />
                                <span>Ready</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Metrics Grid */}
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Total Jobs */}
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-2">
                            <Briefcase className="w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                            {metrics.totalJobs || 0}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">Total Jobs</p>
                    </div>

                    {/* Completion Rate */}
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-2">
                            <CheckCircle className="w-5 h-5 text-gray-400" />
                        </div>
                        <p className={`text-2xl font-bold ${getCompletionRateColor(metrics.completionRate || 0)}`}>
                            {metrics.completionRate || 0}%
                        </p>
                        <p className="text-xs text-gray-600 mt-1">Completion</p>
                    </div>

                    {/* Average Rating */}
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-2">
                            <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                        </div>
                        <p className={`text-2xl font-bold ${getRatingColor(metrics.averageRating || 0)}`}>
                            {metrics.averageRating ? metrics.averageRating.toFixed(1) : '0.0'}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">Avg Rating</p>
                    </div>

                    {/* Days Active */}
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-2">
                            <Calendar className="w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                            {metrics.daysActive || 0}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">Days Active</p>
                    </div>
                </div>

                {/* Detailed Stats */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                            <p className="text-gray-600">Supervised</p>
                            <p className="font-semibold text-gray-900">
                                {metrics.supervisedJobs || 0} jobs
                            </p>
                        </div>
                        <div>
                            <p className="text-gray-600">Independent</p>
                            <p className="font-semibold text-gray-900">
                                {metrics.independentJobs || 0} jobs
                            </p>
                        </div>
                        <div>
                            <p className="text-gray-600">Completed</p>
                            <p className="font-semibold text-gray-900">
                                {metrics.completedJobs || 0} jobs
                            </p>
                        </div>
                    </div>
                </div>

                {/* Eligibility Status */}
                {metrics.criteriaStatus && (
                    <div className="p-4 border-t border-gray-200">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4" />
                            <span>Promotion Progress</span>
                        </h4>
                        <div className="space-y-2">
                            {Object.keys(metrics.criteriaStatus).map((key) => {
                                const status = metrics.criteriaStatus[key];
                                const percentage = (status.current / status.required) * 100;
                                const isMet = status.met;

                                return (
                                    <div key={key}>
                                        <div className="flex items-center justify-between text-sm mb-1">
                                            <span className="text-gray-600 capitalize">
                                                {key.replace(/([A-Z])/g, ' $1').trim()}
                                            </span>
                                            <span className={`font-medium ${isMet ? 'text-green-600' : 'text-gray-900'}`}>
                                                {status.current} / {status.required}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full transition-all ${
                                                    isMet ? 'bg-green-500' : 'bg-blue-500'
                                                }`}
                                                style={{ width: `${Math.min(percentage, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div className="grid grid-cols-2 gap-3">
                        {/* Promote Button */}
                        <button
                            onClick={() => setShowPromotionModal(true)}
                            disabled={phase === 'experienced'}
                            className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                                metrics.eligibleForPromotion
                                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            {metrics.eligibleForPromotion ? (
                                <>
                                    <Award className="w-4 h-4" />
                                    <span>Promote Now</span>
                                </>
                            ) : (
                                <>
                                    <ArrowUp className="w-4 h-4" />
                                    <span>Promote</span>
                                </>
                            )}
                        </button>

                        {/* History Button */}
                        <button
                            onClick={() => setShowHistoryModal(true)}
                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                        >
                            <History className="w-4 h-4" />
                            <span>History</span>
                            {hasPromotionHistory && (
                                <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                    {contractor.promotionHistory.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Promotion Modal */}
            {showPromotionModal && (
                <PromotionModal
                    contractor={contractor}
                    onClose={() => setShowPromotionModal(false)}
                    onPromote={() => {
                        setShowPromotionModal(false);
                        if (onUpdate) onUpdate();
                    }}
                />
            )}

            {/* History Modal */}
            {showHistoryModal && (
                <PromotionHistoryModal
                    contractor={contractor}
                    onClose={() => setShowHistoryModal(false)}
                />
            )}
        </>
    );
}
